import { Sparkles } from "lucide-react";
import { useState } from "react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { PricingModal } from "@/components/PricingModal";
import { useSubscription } from "@/hooks/use-subscription";
import { signInWithGoogle, signOut } from "@/lib/auth";
import type { Session } from "@/hooks/use-session";

interface HeaderProps {
  session: Session | null | undefined;
  isLoadingSession: boolean;
}

export function Header({ session, isLoadingSession }: HeaderProps) {
  const [showPricing, setShowPricing] = useState(false);
  const { subscription, loading: isLoadingSubscription } = useSubscription(session?.user?.id);

  return (
    <header className="fixed top-0 z-50 w-full border-b border-border/10 bg-background/80 backdrop-blur-md">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
        <a href="/" className="flex items-center gap-2.5 transition-transform active:scale-95">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-foreground shadow-lg shadow-foreground/10">
            <Sparkles className="h-5 w-5 text-background" />
          </div>
          <span className="hidden text-xl font-bold tracking-tight text-foreground sm:block">
            VectorAnimation
          </span>
        </a>

        <nav className="flex items-center gap-6">
          {!isLoadingSession && (
            (session === null) || 
            (!isLoadingSubscription && subscription?.plan !== "enterprise")
          ) && (
            <button 
              onClick={() => setShowPricing(true)}
              className="hidden text-sm font-medium text-muted-foreground transition-colors hover:text-foreground md:block"
            >
              {session === null ? "Pricing" : "Upgrade Plan"}
            </button>
          )}

          <div className="h-5 w-px bg-border/40" />

          <ThemeToggle />

          {!isLoadingSession && session === null && (
            <button
              onClick={() => signInWithGoogle()}
              className="inline-flex items-center justify-center rounded-full bg-foreground px-6 py-2.5 text-sm font-semibold text-background transition-opacity hover:opacity-90 active:scale-98"
            >
              Sign in
            </button>
          )}

          {!isLoadingSession && session && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-border/10 bg-muted/50">
                {session.user.image ? (
                  <img
                    src={session.user.image}
                    alt={session.user.name}
                    className="h-6 w-6 rounded-full"
                  />
                ) : (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/20 text-[10px] font-bold text-primary">
                    {session.user.name?.[0]?.toUpperCase() ?? "?"}
                  </div>
                )}
                <span className="hidden text-xs font-medium text-foreground/80 md:block">
                  {session.user.name?.split(' ')[0]}
                </span>
              </div>
              
              <button
                onClick={() => signOut()}
                className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                Sign out
              </button>
            </div>
          )}
        </nav>
      </div>

      <PricingModal 
        open={showPricing} 
        onClose={() => setShowPricing(false)} 
        currentPlan={subscription?.plan} 
      />
    </header>
  );
}
